import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Box, Card, Stack, Button, TextField, Typography } from '@mui/material';

import { createRole } from 'src/services/role.service';

import RolePermissionSelector from './RolePermissionSelector';

export default function RoleCreateView({ onCancel, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    // HARDENED DEFAULT LIST: Full Ticket Lifecycle + Identity Context + UI Access
    permissions: [
      // Dashboard Perspective
      'DASHBOARD:ADMIN',

      // UI Access Defaults
      'TICKETS:UI_ACCESS',
      'MY_GROUPS:UI_ACCESS',

      // Ticket Core
      'TICKET:READ',
      'TICKET:CREATE',
      'TICKET:UPDATE',
      'TICKET:DELETE',
      'TICKET:UPLOAD',

      // Ticket Interactions
      'TICKET_COMMENT:READ',
      'TICKET_COMMENT:CREATE',

      // Ticket Collaboration (All)
      'TICKET_ASSIGNEES:READ',
      'TICKET_ASSIGNEES:CREATE',
      'TICKET_ASSIGNEES:UPDATE',
      'TICKET_ASSIGNEES:DELETE',

      // User & Profile Management
      'USER:READ',
      'USER:UPDATE',

      // Resource Reading
      'DEPARTMENT:READ',
      'TAG:READ',
      'FORM:READ',

      // File Management (All)
      'FILE:READ',
      'FILE:UPLOAD',

      // CSV Data Exchange (All)
      'CSV:UPLOAD',
      'CSV:DOWNLOAD',
    ],
  });

  const handleNext = () => setStep(1);
  const handleBack = () => setStep(0);

  const handleSave = async () => {
    const finalData = {
      ...formData,
      roleName: `ROLE_${formData.roleName.toUpperCase().replace(/\s+/g, '_')}`,
    };
    try {
      await createRole(finalData);
      onSuccess();
    } catch (error) {
      console.error('Role Creation Failed:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4">
          {step === 0
            ? t('rolesPage.newRole')
            : `${t('rolesPage.newRole')}: ${t('rolesPage.label')}`}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button color="inherit" onClick={onCancel}>
            {t('buttons.cancel')}
          </Button>
          {step === 0 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!formData.roleName}
            >
              {t('buttons.next')}
            </Button>
          ) : (
            <>
              <Button onClick={handleBack}>{t('buttons.back')}</Button>
              <Button variant="contained" onClick={handleSave}>
                {t('rolesPage.createRole')}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {step === 0 ? (
        <Card sx={{ p: 5 }}>
          <Stack spacing={4}>
            <TextField
              fullWidth
              label={t('rolesPage.roleName')}
              placeholder="e.g. MANAGER"
              value={formData.roleName}
              onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={6}
              label={t('rolesPage.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Stack>
        </Card>
      ) : (
        <RolePermissionSelector
          selected={formData.permissions}
          onChange={(newPerms) => setFormData({ ...formData, permissions: newPerms })}
        />
      )}
    </Box>
  );
}

RoleCreateView.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
